var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { Grid } from '@material-ui/core';
import { DeviceUtilitiesService, EMPTY_TEXT, FileInput, Magnetic, WhiteLabelMobileButton, useWindowSize } from '@mitchell/react.component.ux';
import { getIsIos, TranslationService } from "../../../common";
import { CAMERA_VIDEO_DIMENSIONS, IntelligentAssignmentSummary, IntelligentPhotoCaptureCameraConstants, PhotoCaptureConstants, PhotoCatureImageConstant, intelligentPhotoCaptureConstants } from '../../constants';
import { IntelligentPhotoCaptureOrientationPermissionStatus, IntelligentPhotoCaptureRotationAngle, PhotoCaptureImageDescription } from '../../enums';
import { getAspectRatio, intelligentPhotoCaptureShutterSound, photoCaptureAllowedMimeType, rotationFromDevice } from '../../utilities';
import { StyledCameraFullView, StyledImageAnalysisMessage, StyledCameraGrid, StyledIconImage, StyledGridWebCamWrapper, StyledCameraButtonsView, StyledIconWrapper, StyledImage, StyledCaptureButton, StyledContentWrapper, HeaderContainer, HeaderDoneButtonWrapper, HeaderSubTitle, StyledVideo } from './IntelligentImageAnalyzerCamera.style';
export const IntelligentEstimatorCameraComponent = ({ onCapture, isImageAnalysisVisible = false, handleFileUpload, backgroundColor, headerText, shouldFlipCamera = false, shouldHideDeviceLibrary = false, appTheme, fileDescription, handleDoneButton, isDoneButtonDisabled = false, shouldHideDoneButton }) => {
    const translateFn = TranslationService.translate;
    const videoPlayer = React.useRef(null);
    const cameraCanvas = React.useRef(null);
    const previewWrapperRef = React.useRef(null);
    const flashTimeoutRef = React.useRef(null);
    const isMountedRef = React.useRef(true);
    const activeVideoTrackRef = React.useRef(null);
    const zoomCapabilityRef = React.useRef(null);
    const currentZoomRef = React.useRef(1);
    const pinchStartDistanceRef = React.useRef(null);
    const pinchStartZoomRef = React.useRef(1);
    const [streamObj, setStreamObj] = React.useState(null);
    const { cameraFlipIcon } = PhotoCatureImageConstant;
    const [iconImage, setIconImage] = React.useState(PhotoCaptureConstants.ALERT_FILL_CIRCLE_ICON_CLASS);
    const [cameraMode, setCameraMode] = React.useState(IntelligentPhotoCaptureCameraConstants.REAR_CAMERA_FACING_MODE);
    const [selectedCamera, setSelectedCamera] = React.useState({});
    const [canvasMessage, setCanvasMessage] = React.useState(IntelligentPhotoCaptureCameraConstants.ANALYZING_IMAGE_MESSAGE);
    const [isFlashActive, setIsFlashActive] = React.useState(false);
    const [rotation, setRotation] = React.useState(IntelligentPhotoCaptureRotationAngle.DEGREE_0);
    const [browserWidth, browserHeight] = useWindowSize();
    const isPortrait = browserHeight > browserWidth;
    const gcdAspectRatio = getAspectRatio(browserWidth / browserHeight, 50);
    const deviceUtility = new DeviceUtilitiesService();
    const updateSelectedCamera = React.useCallback((device) => {
        setSelectedCamera(prev => {
            if (!device || !device.deviceId) {
                return prev;
            }
            if (!prev || !prev.deviceId) {
                return device;
            }
            if (prev.deviceId === device.deviceId) {
                return prev;
            }
            return device;
        });
    }, []);
    const handleDevices = React.useCallback((mediaDevices) => {
        if (getIsIos()) {
            if (deviceUtility.isPhone()) {
                const device = mediaDevices
                    .filter(({ kind, deviceId, label }) => kind === 'videoinput' &&
                    deviceId !== EMPTY_TEXT &&
                    typeof label === 'string' &&
                    label.match(/Back Camera/))
                    .pop();
                updateSelectedCamera(device);
            }
            else {
                const device = mediaDevices
                    .filter(({ kind, deviceId }) => kind === 'videoinput' && deviceId !== EMPTY_TEXT)
                    .pop();
                updateSelectedCamera(device);
            }
        }
        else if (deviceUtility.isMac()) {
            const device = mediaDevices
                .filter(({ kind, deviceId, label }) => kind === 'videoinput' &&
                deviceId !== EMPTY_TEXT &&
                (typeof label !== 'string' || !label.match(/Desk View Camera/)))
                .pop();
            updateSelectedCamera(device);
        }
        else {
            const device = mediaDevices
                .filter(({ kind, deviceId }) => kind === 'videoinput' && deviceId !== EMPTY_TEXT)
                .pop();
            updateSelectedCamera(device);
        }
    }, [updateSelectedCamera]);
    React.useEffect(() => {
        isMountedRef.current = true;
        let isActive = true;
        navigator.mediaDevices
            .enumerateDevices()
            .then(devices => {
            if (isActive && isMountedRef.current) {
                handleDevices(devices);
            }
        })
            .catch(() => undefined);
        return () => {
            isActive = false;
            isMountedRef.current = false;
        };
    }, [streamObj, handleDevices]);
    React.useEffect(() => {
        const requestOrientationPermission = () => __awaiter(void 0, void 0, void 0, function* () {
            if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) {
                return;
            }
            const DeviceOrientationWithPermission = DeviceOrientationEvent;
            try {
                if (DeviceOrientationWithPermission.requestPermission) {
                    const result = yield DeviceOrientationWithPermission.requestPermission();
                    if (result !== IntelligentPhotoCaptureOrientationPermissionStatus.GRANTED && isMountedRef.current) {
                        return;
                    }
                }
            }
            catch (_a) {
                if (isMountedRef.current) {
                    return;
                }
            }
        });
        void requestOrientationPermission();
    }, []);
    React.useEffect(() => {
        const handleOrientation = (event) => {
            var _a, _b, _c;
            const currentScreenAngle = (_c = (_b = (_a = screen.orientation) === null || _a === void 0 ? void 0 : _a.angle) !== null && _b !== void 0 ? _b : window.orientation) !== null && _c !== void 0 ? _c : 0;
            setRotation(rotationFromDevice(event.beta, event.gamma, currentScreenAngle));
        };
        window.addEventListener('deviceorientation', handleOrientation);
        return () => {
            window.removeEventListener('deviceorientation', handleOrientation);
        };
    }, []);
    const getVideoScreenshot = () => {
        if (!videoPlayer.current || !cameraCanvas.current) {
            return null;
        }
        const videoElement = videoPlayer.current;
        const canvasElement = cameraCanvas.current;
        const shouldSwapCanvasDimensions = rotation === IntelligentPhotoCaptureRotationAngle.DEGREE_90 ||
            rotation === IntelligentPhotoCaptureRotationAngle.DEGREE_270;
        const width = videoElement.videoWidth || CAMERA_VIDEO_DIMENSIONS.MIN_WIDTH;
        const height = videoElement.videoHeight || CAMERA_VIDEO_DIMENSIONS.MIN_HEIGHT;
        canvasElement.width = shouldSwapCanvasDimensions ? height : width;
        canvasElement.height = shouldSwapCanvasDimensions ? width : height;
        const context = canvasElement.getContext('2d');
        if (!context) {
            return null;
        }
        context.save();
        context.translate(canvasElement.width / IntelligentPhotoCaptureCameraConstants.HALF_DIVISOR, canvasElement.height / IntelligentPhotoCaptureCameraConstants.HALF_DIVISOR);
        context.rotate((rotation * Math.PI) / IntelligentPhotoCaptureRotationAngle.DEGREE_180);
        context.drawImage(videoElement, -width / IntelligentPhotoCaptureCameraConstants.HALF_DIVISOR, -height / IntelligentPhotoCaptureCameraConstants.HALF_DIVISOR, width, height);
        context.restore();
        return canvasElement.toDataURL('image/jpeg', 1);
    };
    const handleStreamAnalyzerError = () => {
        if (!isMountedRef.current) {
            return;
        }
        setIconImage(PhotoCaptureConstants.ALERT_FILL_CIRCLE_ICON_CLASS);
        setCanvasMessage(IntelligentPhotoCaptureCameraConstants.ERROR_STREAM);
    };
    const getZoomStep = (zoomCapability) => zoomCapability.step || 0.1;
    const clampZoomValue = React.useCallback((zoomValue) => {
        const zoomCapability = zoomCapabilityRef.current;
        if (!zoomCapability) {
            return zoomValue;
        }
        const boundedZoom = Math.min(zoomCapability.max, Math.max(zoomCapability.min, zoomValue));
        const zoomStep = getZoomStep(zoomCapability);
        const zoomStepCount = Math.round((boundedZoom - zoomCapability.min) / zoomStep);
        return Number((zoomCapability.min + zoomStepCount * zoomStep).toFixed(4));
    }, []);
    const updateTrackZoom = React.useCallback((zoomValue) => __awaiter(void 0, void 0, void 0, function* () {
        const track = activeVideoTrackRef.current;
        const zoomCapability = zoomCapabilityRef.current;
        const zoomTrack = track;
        if (!track || !zoomCapability || typeof zoomTrack.applyConstraints !== 'function') {
            return;
        }
        const nextZoomValue = clampZoomValue(zoomValue);
        const zoomStep = getZoomStep(zoomCapability);
        if (Math.abs(nextZoomValue - currentZoomRef.current) < zoomStep / 2) {
            return;
        }
        try {
            yield zoomTrack.applyConstraints({
                advanced: [{ zoom: nextZoomValue }]
            });
            currentZoomRef.current = nextZoomValue;
        }
        catch (_a) {
            return;
        }
    }), [clampZoomValue]);
    const getPrimaryVideoTrack = React.useCallback((stream) => {
        if (typeof stream.getVideoTracks === 'function') {
            const [videoTrack] = stream.getVideoTracks();
            if (videoTrack) {
                return videoTrack;
            }
        }
        const [firstTrack] = stream.getTracks();
        return firstTrack || null;
    }, []);
    const configureTrackZoom = React.useCallback((stream) => {
        const track = getPrimaryVideoTrack(stream);
        const zoomTrack = track;
        activeVideoTrackRef.current = track;
        zoomCapabilityRef.current = null;
        currentZoomRef.current = 1;
        pinchStartZoomRef.current = 1;
        if (!track || typeof zoomTrack.getCapabilities !== 'function') {
            return;
        }
        const capabilities = zoomTrack.getCapabilities();
        const zoomCapability = capabilities && capabilities.zoom;
        if (!zoomCapability || typeof zoomCapability.min !== 'number' || typeof zoomCapability.max !== 'number') {
            return;
        }
        zoomCapabilityRef.current = zoomCapability;
        const currentSettings = typeof zoomTrack.getSettings === 'function' ? zoomTrack.getSettings() : undefined;
        const initialZoomValue = typeof (currentSettings === null || currentSettings === void 0 ? void 0 : currentSettings.zoom) === 'number' ? currentSettings.zoom : zoomCapability.min;
        const clampedInitialZoomValue = clampZoomValue(initialZoomValue);
        currentZoomRef.current = clampedInitialZoomValue;
        pinchStartZoomRef.current = clampedInitialZoomValue;
    }, [clampZoomValue, getPrimaryVideoTrack]);
    const getTouchDistance = (touches) => {
        const horizontalDistance = touches[0].clientX - touches[1].clientX;
        const verticalDistance = touches[0].clientY - touches[1].clientY;
        return Math.hypot(horizontalDistance, verticalDistance);
    };
    const handlePreviewTouchStart = (event) => {
        if (event.touches.length < 2 || !zoomCapabilityRef.current) {
            return;
        }
        event.preventDefault();
        pinchStartDistanceRef.current = getTouchDistance(event.touches);
        pinchStartZoomRef.current = currentZoomRef.current;
    };
    const handlePreviewTouchMove = (event) => {
        if (event.touches.length < 2 || !zoomCapabilityRef.current || !pinchStartDistanceRef.current) {
            return;
        }
        event.preventDefault();
        const updatedTouchDistance = getTouchDistance(event.touches);
        const zoomFactor = updatedTouchDistance / pinchStartDistanceRef.current;
        void updateTrackZoom(pinchStartZoomRef.current * zoomFactor);
    };
    const resetPreviewTouchState = React.useCallback(() => {
        pinchStartDistanceRef.current = null;
        pinchStartZoomRef.current = currentZoomRef.current;
    }, []);
    const closeCamera = React.useCallback(() => {
        if (videoPlayer.current && videoPlayer.current.srcObject) {
            const tracks = videoPlayer.current.srcObject;
            tracks.getTracks().forEach(track => {
                track.stop();
            });
            videoPlayer.current.srcObject = null;
        }
        activeVideoTrackRef.current = null;
        zoomCapabilityRef.current = null;
        currentZoomRef.current = 1;
        resetPreviewTouchState();
        setStreamObj(null);
    }, [resetPreviewTouchState]);
    const openCamera = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            handleStreamAnalyzerError();
            return;
        }
        closeCamera();
        try {
            const constraints = {
                video: {
                    facingMode: 'environment',
                    zoom: true,
                    width: { min: CAMERA_VIDEO_DIMENSIONS.MIN_WIDTH, max: CAMERA_VIDEO_DIMENSIONS.MAX_WIDTH },
                    height: { min: CAMERA_VIDEO_DIMENSIONS.MIN_HEIGHT, max: CAMERA_VIDEO_DIMENSIONS.MAX_HEIGHT }
                }
            };
            const stream = yield navigator.mediaDevices.getUserMedia(constraints);
            setStreamObj({ stream: stream, isStreamAvailable: true });
        }
        catch (error) {
            handleStreamAnalyzerError();
        }
    }), [browserHeight, browserWidth, cameraMode, closeCamera, isPortrait, selectedCamera.deviceId]);
    React.useEffect(() => {
        if (!(streamObj === null || streamObj === void 0 ? void 0 : streamObj.isStreamAvailable) || !streamObj.stream || !videoPlayer.current) {
            return;
        }
        configureTrackZoom(streamObj.stream);
        videoPlayer.current.srcObject = streamObj.stream;
        videoPlayer.current.play().catch(() => undefined);
    }, [configureTrackZoom, streamObj]);
    React.useEffect(() => {
        const previewWrapperElement = previewWrapperRef.current;
        if (!previewWrapperElement) {
            return;
        }
        const preventGestureDefault = (event) => {
            event.preventDefault();
        };
        previewWrapperElement.addEventListener('gesturestart', preventGestureDefault, { passive: false });
        previewWrapperElement.addEventListener('gesturechange', preventGestureDefault, { passive: false });
        previewWrapperElement.addEventListener('gestureend', preventGestureDefault, { passive: false });
        return () => {
            previewWrapperElement.removeEventListener('gesturestart', preventGestureDefault);
            previewWrapperElement.removeEventListener('gesturechange', preventGestureDefault);
            previewWrapperElement.removeEventListener('gestureend', preventGestureDefault);
        };
    }, []);
    React.useEffect(() => {
        openCamera();
        return () => closeCamera();
    }, [openCamera, closeCamera]);
    const handleCameraCapture = () => {
        if (fileDescription === PhotoCaptureImageDescription.SUMMARY) {
            setIsFlashActive(true);
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
            }
            flashTimeoutRef.current = setTimeout(() => {
                setIsFlashActive(false);
                flashTimeoutRef.current = null;
            }, IntelligentPhotoCaptureCameraConstants.FLASH_ACTIVE_TIMEOUT_MS);
        }
        if (videoPlayer.current) {
            const capturedImage = getVideoScreenshot();
            if (capturedImage) {
                try {
                    intelligentPhotoCaptureShutterSound();
                }
                catch (error) {
                    // Shutter sound is optional; capture must continue even if audio fails.
                    void error;
                }
                onCapture(capturedImage);
            }
        }
    };
    React.useEffect(() => {
        return () => {
            if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
                flashTimeoutRef.current = null;
            }
        };
    }, []);
    const onChange = (filesList) => __awaiter(void 0, void 0, void 0, function* () {
        const files = filesList;
        if (files && files.length) {
            const imagefile = files[0];
            handleFileUpload && handleFileUpload(imagefile);
        }
    });
    const handleSwitchCameraMode = () => setCameraMode((cameraMode) => cameraMode === IntelligentPhotoCaptureCameraConstants.REAR_CAMERA_FACING_MODE
        ? IntelligentPhotoCaptureCameraConstants.FRONT_CAMERA_FACING_MODE
        : IntelligentPhotoCaptureCameraConstants.REAR_CAMERA_FACING_MODE);
    const getAlignment = (isAlignment) => {
        if (shouldFlipCamera && !shouldHideDeviceLibrary) {
            return isAlignment ? 'center' : 4;
        }
        else if (shouldFlipCamera && shouldHideDeviceLibrary) {
            return isAlignment ? 'flex-end' : 6;
        }
        else if (!shouldFlipCamera && !shouldHideDeviceLibrary) {
            return isAlignment ? 'center' : 6;
        }
        return isAlignment ? 'center' : 12; // Default value if no condition matches
    };
    const divImageWrapper = () => {
        return (React.createElement(StyledIconWrapper, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-upload-button`, className: PhotoCaptureConstants.ADD_PHOTO_ICON_CLASS }));
    };
    return (React.createElement(StyledContentWrapper, { bgcolor: backgroundColor },
        React.createElement(StyledCameraFullView, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-intelligent-webcam`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-intelligent-webcam`, aspectratio: gcdAspectRatio[0] / gcdAspectRatio[1] },
            React.createElement(HeaderContainer, null, fileDescription === PhotoCaptureImageDescription.SUMMARY ? (React.createElement(React.Fragment, null, !shouldHideDoneButton && (React.createElement(HeaderDoneButtonWrapper, null,
                React.createElement(WhiteLabelMobileButton, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-summary-done-button`, label: translateFn(IntelligentAssignmentSummary.DONE_BUTTON_LABEL), buttonBackgroundColor: isDoneButtonDisabled
                        ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonBackgroundColor) || Magnetic.colorBrand50
                        : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonBackgroundColor) || Magnetic.colorBrand50, buttonTextColor: isDoneButtonDisabled
                        ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonTextColor) || Magnetic.colorWhite
                        : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonTextColor) || Magnetic.colorWhite, isDisabled: isDoneButtonDisabled, onClick: () => handleDoneButton && handleDoneButton() }))))) : (React.createElement(React.Fragment, null,
                React.createElement(HeaderSubTitle, { id: "camera_HeaderSubTitle" }, headerText && translateFn(headerText)),
                streamObj && streamObj.isStreamAvailable && isImageAnalysisVisible && (React.createElement(StyledImageAnalysisMessage, { container: true, wrap: "nowrap", justify: "center", alignItems: "flex-start" },
                    React.createElement(StyledIconImage, { className: iconImage }),
                    translateFn(canvasMessage)))))),
            React.createElement(Grid, { container: true, justify: "center", direction: "column" },
                React.createElement(StyledGridWebCamWrapper, { container: true, item: true, isFlashActive: isFlashActive, ref: previewWrapperRef, onTouchStart: handlePreviewTouchStart, onTouchMove: handlePreviewTouchMove, onTouchEnd: resetPreviewTouchState, onTouchCancel: resetPreviewTouchState },
                    React.createElement(StyledVideo, { id: 'webcam-component', autoPlay: true, playsInline: true, muted: true, ref: videoPlayer }),
                    React.createElement("canvas", { ref: cameraCanvas, style: { display: 'none' } })),
                React.createElement(StyledCameraButtonsView, { container: true, wrap: "nowrap" },
                    !shouldHideDeviceLibrary && (React.createElement(Grid, { item: true, xs: (shouldFlipCamera && 4) || 6 },
                        React.createElement(FileInput, { acceptedMimeTypes: photoCaptureAllowedMimeType, disabled: false, multiple: false, onSelected: onChange, element: divImageWrapper(), id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-fileInput` }))),
                    React.createElement(Grid, { id: "alignment-grid", item: true, container: shouldFlipCamera || shouldHideDeviceLibrary, alignItems: shouldFlipCamera || shouldHideDeviceLibrary ? 'center' : undefined, justify: getAlignment(true), xs: getAlignment(false) }, fileDescription === PhotoCaptureImageDescription.SUMMARY ? (React.createElement(WhiteLabelMobileButton, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, label: EMPTY_TEXT, className: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_CLASS_NAME, buttonBackgroundColor: isFlashActive
                            ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonBackgroundColor) || Magnetic.colorBrand50
                            : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonBackgroundColor) || Magnetic.colorBrand50, buttonTextColor: isFlashActive
                            ? (appTheme === null || appTheme === void 0 ? void 0 : appTheme.disabledButtonTextColor) || Magnetic.colorWhite
                            : (appTheme === null || appTheme === void 0 ? void 0 : appTheme.buttonTextColor) || Magnetic.colorWhite, isDisabled: isFlashActive, onClick: handleCameraCapture, icon: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_ICON })) : (React.createElement(StyledCaptureButton, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-capture-button`, icon: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_ICON, className: IntelligentPhotoCaptureCameraConstants.CAPTURE_BUTTON_CLASS_NAME, onClick: handleCameraCapture, isHoverEffectDisabled: true }))),
                    shouldFlipCamera && (React.createElement(Grid, { item: true, container: true, alignItems: "center", justify: "flex-end", xs: 4 },
                        React.createElement(StyledIconWrapper, { id: `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-flip-button`, "data-testid": `${intelligentPhotoCaptureConstants.MODULE_PREFIX}-flip-button`, onClick: handleSwitchCameraMode },
                            React.createElement(StyledImage, { src: cameraFlipIcon.image })))))),
            !streamObj && (React.createElement(StyledCameraGrid, { container: true, justify: "center", alignItems: "center", direction: "column" }, translateFn(IntelligentPhotoCaptureCameraConstants.NO_STREAM_ERROR))))));
};
//# sourceMappingURL=IntelligentEstimatorCameraComponent.js.map